import 'core-js/library/es6/promise';
import noop from 'lodash/noop';
import get from 'lodash/get';
import difference from 'lodash/difference';
import parallel from 'async/parallel';

import '!!style-loader!css-loader!sass-loader!./scss/index.scss';
import { trackingFrameClassName } from '../libs/premiumFeatures';
import { post } from '../../../common/libs/api';

//set in embed script
const SCRIPT_TAG_ID = 'scorestream-vertScoreboard';
const currentScript = document.getElementById(SCRIPT_TAG_ID);

function injectScript (ifr, attrs, callback = noop) {
	const script = document.createElement('script');
	Object.keys(attrs)
		.forEach(attr => script[attr] = attrs[attr]);
	script.onload = callback;
	ifr.contentDocument.body.appendChild(script);
}

function injectLink (ifr, attrs) {
	const link = document.createElement('link');
	Object.keys(attrs)
		.forEach(attr => link[attr] = attrs[attr]);
	ifr.contentDocument.head.appendChild(link);
}

const fonts = [
	'//fonts.googleapis.com/css?family=Open+Sans+Condensed:300,700',
	'//fonts.googleapis.com/css?family=Open+Sans:700,300,800,400',
	'//fonts.googleapis.com/css?family=Lato:900',
	"//maxcdn.bootstrapcdn.com/font-awesome/4.3.0/css/font-awesome.min.css",
];

const styles = [
	"https://cdnjs.cloudflare.com/ajax/libs/sweetalert/0.4.2/sweet-alert.min.css",
];

const asyncScripts = [
	"https://www.googletagservices.com/tag/js/gpt.js",
];

const scripts = [
	"https://cdnjs.cloudflare.com/ajax/libs/async/0.9.0/async.min.js",
	"https://cdnjs.cloudflare.com/ajax/libs/lodash.js/3.10.1/lodash.min.js",
	"https://cdnjs.cloudflare.com/ajax/libs/moment.js/2.10.3/moment.min.js",
	"https://cdnjs.cloudflare.com/ajax/libs/sweetalert/0.4.2/sweet-alert.min.js"
];

const omittedDataAttrs = [
	'src',
	'ss_widget_type',
	'widgetData',
	'widgetType',
	'uiVer',
];

function parseValue (val) {
	try {
		return JSON.parse(val);
	} catch (e) {
		return val;
	}
}

function loadRegularIframe (el, ifr, userWidgetId, domain, widgetData) {
	if (userWidgetId === '__manager') {
		ifr.src = `${domain}/widgets/scoreboards/vert?widgetData=${encodeURIComponent(JSON.stringify({asyncWidgetData: true}))}`;
		el.appendChild(ifr);
		ifr.addEventListener('load', () => {
			ifr.contentWindow.postMessage({widgetData}, '*');
		});
	} else {
		ifr.src = `${domain}/widgets/scoreboards/vert?userWidgetId=${userWidgetId}`;
		el.appendChild(ifr);
	}
}

function loadSrclessIframe (el, ifr, userWidgetId, widgetData, domain, cdnHost, uiVer) {
	const trackingIfr = document.createElement('iframe');
	trackingIfr.setAttribute('style', 'height:0;width:0;border:none;');
	trackingIfr.setAttribute('src', `${domain}/widgets/scoreboards/newVert?userWidgetId=${userWidgetId}`);
	//the className that gets used here is actually important, and should not be changed -- the widget code (realtime/widgets, and the premiumFeatures lib on ui/widgets) uses the className when testing if it's in a cross origin frame
	trackingIfr.className = trackingFrameClassName;
	el.appendChild(trackingIfr);

	ifr.srcdoc = `
				<!doctype html>
				<html lang="en">
					<head>
						<meta charset="utf-8" />
						<meta name="viewport" content="width=device-width, initial-scale=1.0" />
					</head>
					<body style="max-width: 100vw !important;">
					</body>
				</html>
			`.replace(/\n/g, '');
	el.appendChild(ifr);
	ifr.onload = function () {
		ifr.contentDocument.body.setAttribute('style', 'max-width: 100vw !important;');

		const container = document.createElement('div');
		container.setAttribute('class', 'scorestream-widget-container');
		container.dataset.type = 'vert';
		container.dataset.widgetid = userWidgetId;
		container.setAttribute('style', 'width:100%;height:100%;');

		const gData = document.createElement('script');
		gData.innerHTML = `
				var gData = {
					ssApiDomain: ${JSON.stringify(domain)},
					widgetData: ${JSON.stringify(widgetData)}
				};
			`;
		ifr.contentDocument.body.appendChild(gData);

		ifr.contentDocument.body.appendChild(container);
		fonts.forEach(url => injectLink(ifr, {
			href: url,
			type: 'text/css',
			rel: 'stylesheet',
		}));
		styles.forEach(url => injectLink(ifr, {
			href: url,
			type: 'text/css',
			rel: 'stylesheet',
		}));
		injectLink(ifr, {
			href: `${cdnHost}/cdn/css/widgets/scoreboards/vert.css`,
			type: 'text/css',
			rel: 'stylesheet',
		});

		asyncScripts.forEach(url => injectScript(ifr, {
			src: url,
			async: 'async',
		}));

		const tasks = scripts.map(url => {
			return callback => injectScript(ifr, {
				src: url,
			}, () => callback());
		});
		tasks.push(callback => {
			injectScript(ifr, {
				src: "https://cdnjs.cloudflare.com/ajax/libs/jquery/2.1.4/jquery.min.js",
			}, () => injectScript(ifr, {
				src: "https://cdnjs.cloudflare.com/ajax/libs/jquery-cookie/1.4.1/jquery.cookie.min.js",
			}, () => callback()));
		});
		tasks.push(callback => {
			injectScript(ifr, {
				src: "https://cdnjs.cloudflare.com/ajax/libs/dustjs-linkedin/2.7.2/dust-core.min.js",
			}, () => injectScript(ifr, {
				src: "https://cdnjs.cloudflare.com/ajax/libs/dustjs-helpers/1.7.1/dust-helpers.min.js",
			}, () => callback()));
		});

		parallel(tasks, () => {
			injectScript(ifr, {
				src: `${cdnHost}/cdn/js/bundles/vertBundle.js?uiVer=${uiVer}`,
			});
		});
	};
}

function loadWidgetData (userWidgetId, domain) {
	return post({
		domain,
		method: 'users.widgets.get',
		params: {userWidgetIds: [parseInt(userWidgetId, 10)]},
	}).then(result => {
		return get(result, 'collections.userWidgetCollection.list[0]', {});
	});
}

[...document.getElementsByClassName('scorestream-widget-container')]
	.filter(el => el.dataset.widgetType === 'vertScoreboard')
	.forEach(/**@type HTMLElement*/el => {
		if (!el.childElementCount) {
			const ifr = document.createElement('iframe');
			ifr.classList.add('scorestreamIframe');
			const userWidgetId = el.dataset.userWidgetId;
			const cdnHost = currentScript.dataset.cdnhost;
			const domain = currentScript.dataset.domain;
			const uiVer = el.dataset.uiVer;

			let widgetData = difference(Object.getOwnPropertyNames(el.dataset), omittedDataAttrs)
				.reduce((acc, attr) => ({
					...acc,
					[attr]: parseValue(el.dataset[attr]),
				}), {});
			if (el.dataset.widgetData) {
				widgetData = {
					...parseValue(el.dataset.widgetData),
					...widgetData,
				};
			}

			if (userWidgetId !== '__manager') {
				widgetData = null;
			}

			let widgetDataPromise;
			if (!widgetData) {
				widgetDataPromise = loadWidgetData(userWidgetId, domain);
			} else {
				widgetDataPromise = Promise.resolve(widgetData);
			}
			widgetDataPromise.then(widgetData => {
				if (!widgetData.useSrclessIframe) {
					loadRegularIframe(el, ifr, userWidgetId, domain, widgetData);
				} else {
					loadSrclessIframe(el, ifr, userWidgetId, widgetData, domain, cdnHost, uiVer);
				}

			});

		}
	});
